import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, of, switchMap, take } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Attaches `Authorization: Bearer <access_token>` to backend API calls.
 *
 * We only intercept requests targeting `environment.apiBaseUrl` to avoid
 * affecting asset/CDN calls.
 */
export const auth0TokenInterceptor: HttpInterceptorFn = (req, next) => {
  // If Authorization is already set, don't override it.
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  const baseUrl = (environment.apiBaseUrl ?? '').replace(/\/+$/, '');
  const url = req.url;

  // Attach tokens to all backend API requests:
  // - Absolute: `${apiBaseUrl}/api/...`
  // - Relative: `/api/...` (when apiBaseUrl is empty in some deployments)
  const isApiRequest =
    (baseUrl && url.startsWith(`${baseUrl}/api/`)) ||
    (!baseUrl && url.startsWith('/api/'));

  if (!isApiRequest) return next(req);

  const auth = inject(AuthService);

  return auth
    .getAccessTokenSilently({
      authorizationParams: {
        audience: environment.auth0.audience,
      },
    })
    .pipe(
      take(1),
      switchMap((token) => {
        const authed = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
        return next(authed);
      }),
      // If we can't get a token (e.g. unauthenticated), proceed without auth.
      catchError((error) => {
        next(req);
        console.error('Error getting access token', error);
        return of(error);
      }),
    );
};

